import React, { useEffect, useState } from "react";
import AppCard from "../../../shared/components/AppCard";
import {
  Button,
  Table,
  Modal,
  Popconfirm,
  Tooltip,
  Upload,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "./ReportMacrosList.scss";
import { ReportMacro, isOrgMacro } from "../../../models/ReportMacro/reportMacro.model";
import ReportMacroForm from "../ReportMacroForm";
import ReportMacroService from "../../../services/ReportMacro/reportMacro.service";
import AppLoader from "../../../shared/components/AppLoader";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import { Doctor } from "../../../models/Doctor/doctor.model";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";

interface ReportMacrosListProps extends AuthReducerProps {}

function ReportMacrosList({ userRole, user }: ReportMacrosListProps) {
  const [reportMacros, setReportMacros] = useState<ReportMacro[]>([]);
  const [showMacroForm, setShowMacroForm] = useState(false);
  const [activeMacro, setActiveMacro] = useState(new ReportMacro());
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const handleCloseMacroForm = () => setShowMacroForm(false);

  const handleAddMacro = () => {
    setActiveMacro(new ReportMacro());
    setShowMacroForm(true);
  };

  const handleMacroFormSuccess = () => {
    setShowMacroForm(false);
    handleFetchReportMacros();
  };

  const handleFetchReportMacros = () => {
    setLoading(true);
    ReportMacroService.fetchReportMacros(
      userRole || UserRoleEnum.ADMIN,
      (macros: ReportMacro[]) => {
        setReportMacros(macros);
        setLoading(false);
      },
      () => setLoading(false),
      () => setLoading(false)
    );
  };

  const canEditMacro = (macro: ReportMacro) => {
    if (userRole === UserRoleEnum.ADMIN) return isOrgMacro(macro);
    if (userRole === UserRoleEnum.DOCTOR) return macro.doctorId === (user as Doctor)?.id;
    return false;
  };

  const handleDeleteMacro = (macro: ReportMacro) => {
    if (!macro.id) return;
    ReportMacroService.deleteReportMacro(
      userRole || UserRoleEnum.ADMIN,
      macro.id,
      () => handleFetchReportMacros(),
      () => {},
      () => {}
    );
  };

  const handleBulkUpload = (file: File) => {
    setUploadLoading(true);
    ReportMacroService.bulkUploadReportMacros(
      file,
      () => {
        handleFetchReportMacros();
        setUploadLoading(false);
      },
      () => setUploadLoading(false),
      () => setUploadLoading(false)
    );
    return false; // Prevent default upload behavior
  };

  useEffect(() => {
    handleFetchReportMacros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: "Shortname",
      dataIndex: "shortname",
      key: "shortname",
      render: (text: string) => <code>{text}</code>,
    },
    {
      title: "Expanded text",
      dataIndex: "expandedText",
      key: "expandedText",
      ellipsis: true,
      render: (text: string) =>
        text?.length > 80 ? `${text.substring(0, 80)}...` : text,
    },
    ...(userRole === UserRoleEnum.DOCTOR
      ? [
          {
            title: "Source",
            key: "source",
            render: (_: any, macro: ReportMacro) =>
              isOrgMacro(macro) ? (
                <span className="report-macro-source-org">Organization</span>
              ) : (
                <span className="report-macro-source-personal">Personal</span>
              ),
            width: 110,
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      render: (_: any, macro: ReportMacro) =>
        canEditMacro(macro) ? (
          <div className="report-macro-list_actions">
            <Tooltip title="Edit macro">
              <EditOutlined
                onClick={() => {
                  setShowMacroForm(true);
                  setActiveMacro(macro);
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this macro?"
              onConfirm={() => handleDeleteMacro(macro)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete macro">
                <DeleteOutlined className="ml-2" />
              </Tooltip>
            </Popconfirm>
          </div>
        ) : (
          <span className="text-muted">—</span>
        ),
      width: 120,
    },
  ];

  return (
    <div className="report-macro-list">
      <AppCard>
        <h2>
          Report Macros (Autocomplete Shortcuts)
          <div className="float-right d-flex align-items-center gap-2">
            {userRole === UserRoleEnum.ADMIN && (
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={handleBulkUpload}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={uploadLoading}
                  disabled={uploadLoading}
                >
                  Bulk Upload Excel
                </Button>
              </Upload>
            )}
            <Button type="primary" onClick={handleAddMacro}>
              <PlusOutlined />
              Add Macro
            </Button>
          </div>
        </h2>
        <div className="report-macro-list__note mb-3">
          <strong>Note:</strong> Macros work only in the new editor. Toggle{" "}
          <strong>Use New Editor</strong> when generating a report to use this feature.
        </div>
        <p className="text-muted mb-3">
          Add shortcuts for the report editor. Type <code>#shortname</code> while
          writing to autocomplete. Admin-created macros are shared with all
          doctors; doctor-created macros are personal.
        </p>
        {userRole === UserRoleEnum.ADMIN && (
          <p className="text-muted mb-3">
            <strong>Bulk upload:</strong> Use an Excel file with columns{" "}
            <code>shortname</code> and <code>expanded_text</code>.
          </p>
        )}
        {loading ? (
          <AppLoader loading={loading} />
        ) : (
          <Table
            dataSource={reportMacros}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
          />
        )}
      </AppCard>
      <Modal
        className="primary-modal"
        footer=""
        keyboard={false}
        maskClosable={false}
        destroyOnClose
        visible={showMacroForm}
        onCancel={handleCloseMacroForm}
      >
        {activeMacro && (
          <ReportMacroForm
            reportMacro={activeMacro}
            onSuccess={handleMacroFormSuccess}
          />
        )}
      </Modal>
    </div>
  );
}

export default AuthContainer(ReportMacrosList);
